import { LitElement, html, css } from 'lit';
import { getCookie, clearCookie } from '../../utilities/cookies';
import { accessibility, formStyles } from '../../utilities/styles';
import { EventManager } from '../../utilities/events';
import { hideRevealInputs, lockForm, releaseFormLock, releaseFormLockwithBeacon, checkFormLock } from '../../utilities/formsUtilities';
import { handle403s } from '../../utilities/AllInErrorHandling';
export class AllInPagedForm extends LitElement {
    static properties = {
        action: { type: String },
        restUrl: { type: String },
        nonce: { type: String },
        alreadyLoggedInMessage: { type: String },
        loggedIn: { type: String },
        redirect: { type: String },
        redirectOnSave: { type: String },
        submitButton: { type: String },
        name: { type: String },
        nextButton: { type: String },
        uid: { type: String },
        userid: { type: String },
        childPageCount: { type: Number },
        prefill: { type: String },
        clearCookies: { type: String },
        getSaved: { type: String },
        useRemoteStorage: { type: String },
        setPage: { type: String },
        hideProgress: { type: String },
        hardlinks: { type: Object },
        _successMessage: { state: true },
        _errorMessage: { state: true },
        _pages: { state: true, type: Array },
        _currentPage: { state: true, type: Number },
        _isLastPage: { state: true },
        _submitButton: { state: true },
        _nextButton: { state: true },
        switchflowbox: { type: String },
    };
    constructor() {
        super();
        this._errorMessage = '';
        if (!this.redirect) {
            this.redirect = '/';
        }
        if (!this.redirectOnSave) {
            this.redirectOnSave = '/';
        }
        this._isLastPage = false;
        this._filesMap = new Map();
        // Bind once so the same reference can be used for add/removeEventListener
        this._onPageValidity = this._onPageValidity.bind(this);
        this._onFormValidity = this._onFormValidity.bind(this);
    }
    connectedCallback() {
        super.connectedCallback();
        this.eventManager = new EventManager(this);
        this.canRefreshSession = false;
        this._initSessionTimer();
        let addNewCookie = getCookie('addnew');
        if (addNewCookie && addNewCookie == 'true') {
            this._clearLocalStorage();
            clearCookie('addnew');
            let url = new URL(window.location.href);
            url.searchParams.delete('addnew');
            window.history.replaceState({}, '', url);
        }
        this.isConstructed = false;
        this.loading = true;
    }
    async firstUpdated(changedProperties) {
        //Have to make sure all the sub components are loaded before we can construct the form
        if (this.isConstructed == false) {
            this.loading = true;
            if (!this.form) {
                await new Promise((resolve) => requestAnimationFrame(resolve));
            }
            const childrenSlotted = this._slottedChildren;
            if (childrenSlotted.length <= this.childPageCount) {
                await new Promise((resolve) => {
                    window.onload = resolve;
                });
            }
            const allChildren = [];
            childrenSlotted.forEach((el) => {
                let children = Array.from(el.querySelectorAll('allin-paged-form-page'));
                allChildren.push(...children);
            });
            await new Promise((resolve) => requestAnimationFrame(resolve));
            await Promise.all(allChildren.map((el) => {
                el.updateComplete;
            }));
            if (this.useRemoteStorage) {
                let isLocked = await checkFormLock(this.useRemoteStorage, this.userid, this.restUrl, this.nonce);
                if (isLocked) {
                    window.location.href = `/organisation-dashboard?form_locked=${this.useRemoteStorage}`;
                }
                else {
                    lockForm(this.useRemoteStorage, this.userid, this.restUrl, this.nonce);
                    await this._getRemoteStorage();
                }
                addEventListener('visibilitychange', (event) => {
                    if (document.visibilityState === 'hidden') {
                        releaseFormLockwithBeacon(this.useRemoteStorage, this.userid, this.restUrl, this.nonce);
                    }
                });
            }
            this._constructForm();
            this._constructPages();
            this._getCurrentPage();
            this.isConstructed = true;
            this.loading = false;
        }
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        if (this.useRemoteStorage) {
            releaseFormLock(this.useRemoteStorage, this.userid, this.restUrl, this.nonce);
        }
    }
    get form() {
        return this._slottedChildren.find(el => el.tagName == 'FORM');
    }
    get formResponse() {
        return this.shadowRoot.querySelector('.message');
    }
    get _slottedChildren() {
        const slot = this.renderRoot.querySelector('slot');
        if (!slot)
            return [];
        return slot.assignedElements({ flatten: true });
    }
    get review() {
        let review = this.form.querySelector('allin-paged-form-review');
        return review;
    }
    get _successMessage() {
        return this.form.querySelector('allin-paged-form-success');
    }
    get _container() {
        return this.renderRoot.querySelector('#container');
    }
    async _subPageMasters() {
        let pages = this.form.querySelectorAll('allin-paged-form-page');
        let masters = [];
        let dependencies = [];
        let parents = [];
        let masterParents = [];
        pages.forEach(async (page) => {
            let slottedChildren = page.renderRoot.querySelector('slot');
            if (slottedChildren) {
                slottedChildren = slottedChildren.assignedElements({ flatten: true });
                slottedChildren.forEach(async (child) => {
                    if (child.tagName == 'DIV') {
                        let m = child.querySelectorAll('.has_dependency');
                        masters = [...masters, ...m];
                        let d = child.querySelectorAll('allin-paged-form-subpage');
                        dependencies = [...dependencies, ...d];
                        if (m.length && !masterParents.includes(page)) {
                            masterParents.push(page);
                        }
                    }
                    if (child.tagName == 'ALLIN-PAGED-FORM-SUBPAGE') {
                        dependencies = [...dependencies, child];
                        if (!parents.includes(page)) {
                            parents.push(page);
                        }
                    }
                });
            }
        });
        await Promise.all(dependencies.map((el) => {
            el.updateComplete;
        }));
        return { 'masters': masters, 'dependencies': dependencies, 'parents': parents, 'masterParents': masterParents };
    }
    render() {
        return html ` \n            <div id="container" class="${this.loading ? 'loading' : ''}">\n                <slot></slot>\n                <div class="spinner"></div>\n            </div>\n        `;
    }
    async changePage(index, save = true) {
        if (this.hideProgress && this.hideProgress == 'true') {
            save = false;
        }
        this.form.classList.add('loading');
        index = parseInt(index);
        this._currentPage = index;
        this.pageInput.value = index;
        // if(index==this._pages.length-1){
        //     this._isLastPage = true;
        //     if(this._saveButtonOnScroll){
        //         this._saveButtonOnScroll.innerText = msg(str`Submit`);
        //     }
        //     if(this._saveButton){
        //         this._saveButton.innerText = msg(str`Submit`);
        //     }
        // }else{
        //     this._isLastPage = false;
        // }
        // Determine if this is the last page
        this._isLastPage = index === this._pages.length - 1;
        // Update save button text depending on page
        if (this._saveButton) {
            this._saveButton.innerText = this._isLastPage
                ? `Submit` : this._saveButtonDefaultText;
        }
        if (this._saveButtonOnScroll) {
            this._saveButtonOnScroll.innerText = this._isLastPage
                ? `Submit` : this._saveButtonDefaultText;
        }
        if (save) {
            let response = await this._saveToRemoteStorage(false);
            if (response.ok || response.partial) {
                if (response.id) {
                    this._addLocationField(response.id);
                }
                if (response.org_id) {
                    this._addOrganisationField(response.org_id);
                }
                if (response.zone_id) {
                    this._addZoneField(response.zone_id);
                }
                if (response.membership_id) {
                    this._addMembershipField(response.membership_id);
                }
            }
            if (response.error) {
                this._errorMessage.innerHTML = response.message;
                this._errorMessage.classList.add('error');
                this.form.classList.remove('loading');
                return;
            }
            this._saveToLocalStorage();
        }
        this._pages.forEach((el, i) => {
            el = el.el;
            if (i == index) {
                el.classList.remove('slide-off');
                el.classList.add('current');
                el.current = true;
                el.focus();
            }
            else {
                el.classList.remove('current');
                el.current = false;
                el.classList.add('slide-off');
            }
        });
        this._checkForValidSubPages();
        this._setSubmitButton();
        this._triggerReviewUpdate();
        this._dispatchCurrentPage();
        this._showHideBackButton();
        hideRevealInputs(this._pages[index].el);
        this.scrollIntoView({ behavior: "instant", block: "start" });
        this._refreshUserSession();
        if (this.useRemoteStorage) {
            lockForm(this.useRemoteStorage, this.userid, this.restUrl, this.nonce);
        }
        this.form.classList.remove('loading');
        this._saveButton.disabled = false;
        if (this._switchFlowBox) {
            this._switchFlowBox.style.display =
                index === 0 ? '' : 'none';
        }
    }
    async changeSubPage(index, parentIndex) {
        let parentPage = this._pages[parentIndex].el;
        this.form.classList.add('loading');
        let response = await this._saveToRemoteStorage(false);
        if (response.ok) {
            if (response.id) {
                this._addLocationField(response.id);
            }
            if (response.org_id) {
                this._addOrganisationField(response.org_id);
            }
            if (response.zone_id) {
                this._addZoneField(response.zone_id);
            }
        }
        if (response.error) {
            this._errorMessage.innerHTML = response.message;
            this._errorMessage.classList.add('error');
            this.form.classList.remove('loading');
            return;
        }
        this._saveToLocalStorage();
        parentPage.setCurrentSubPage(index);
        this.form.classList.remove('loading');
    }
    _submit(e) {
        e.preventDefault();
        if (this._isLastPage) {
            this._handleSubmit(e);
        }
        else {
            this._handleNextPage(e);
        }
    }
    _dispatchPageData() {
        this.eventManager.emit('allin-paged-form-page-data', { name: this.name, data: this._pages, el: this });
    }
    _dispatchCurrentPage() {
        this.eventManager.emit('allin-paged-form-current-page', { name: this.name, index: this._currentPage });
    }
    _dispatchSuccess() {
        let message = this._successMessage.getAttribute('title') ? this._successMessage.getAttribute('title') : `Thank you`;
        this.eventManager.emit('allin-paged-form-success', { title: message });
    }
    _getCurrentPage() {
        let currentPage = this.setPage ? this.setPage : window.localStorage.getItem(`allin-paged-form-${this.name}-current-page-${this.uid}`);
        let formData = JSON.parse(localStorage.getItem(`allin-paged-form-${this.name}-${this.uid}`));
        if (formData && formData.hasOwnProperty('location_id')) {
            this._addLocationField(formData.location_id);
        }
        if (formData && formData.hasOwnProperty('org_id')) {
            this._addOrganisationField(formData.org_id);
        }
        if (formData && formData.hasOwnProperty('id') && formData.hasOwnProperty('zoneType')) {
            this._addZoneField(formData.id);
        }
        if (currentPage == 'success') {
            let link = window.localStorage.getItem(`allin-paged-form-${this.name}-current-page-${this.uid}-link`);
            if (link) {
                link = JSON.parse(link);
            }
            this._submissionSuccess(false, link);
        }
        else if (currentPage) {
            this._successMessage?.classList.add('slide-off');
            this.changePage(currentPage, false);
        }
        else {
            this._successMessage?.classList.add('slide-off');
            this.changePage(0, false);
        }
    }
    _constructForm() {
        let form = this.form;
        let submit = document.createElement('input');
        submit.type = 'submit';
        submit.value = this.submitButton;
        form.appendChild(submit);
        this._submitButton = submit;
        let button = document.createElement('button');
        button.textContent = this.nextButton;
        button.addEventListener('click', this._handleNextPage.bind(this));
        this._nextButton = button;
        let backButton = document.createElement('button');
        backButton.textContent = `Back`;
        backButton.addEventListener('click', this._handlePrevPage.bind(this));
        backButton.classList.add('back-button');
        this._backButton = backButton;
        let buttonContainer = document.createElement('div');
        buttonContainer.classList.add('button-container');
        buttonContainer.appendChild(backButton);
        buttonContainer.appendChild(button);
        form.appendChild(buttonContainer);
        if (!this.hideProgress && this.hideProgress != 'true') {
            // let saveButton = document.createElement('button');
            // saveButton.textContent = msg(str`Save progress`);
            // saveButton.addEventListener('click',this._handleSave.bind(this));
            // this._saveButton = saveButton;
            let saveButton = document.createElement('button');
            this._saveButtonDefaultText = `Save progress`; // store default label
            saveButton.textContent = this._saveButtonDefaultText;
            saveButton.addEventListener('click', this._handleSave.bind(this));
            this._saveButton = saveButton;
            let buttonContainer_2 = document.createElement('div');
            buttonContainer_2.classList.add('button-container', 'save-container');
            if (this.switchflowbox) {
                const box = document.createElement('div');
                box.classList.add('not-a-member-link');
                box.innerHTML = this.switchflowbox;
                this._switchFlowBox = box; // store a reference
                buttonContainer_2.prepend(box);
                // Only show on the first page
                this._switchFlowBox.style.display = this._currentPage === 0 ? '' : 'none';
            }
            buttonContainer_2.appendChild(saveButton);
            let saveButtonOnScroll = saveButton.cloneNode(true);
            saveButtonOnScroll.classList.add('save-button-on-scroll', 'hide');
            saveButtonOnScroll.addEventListener('click', this._handleSave.bind(this));
            buttonContainer_2.appendChild(saveButtonOnScroll);
            this._saveButtonOnScroll = saveButtonOnScroll;
            form.appendChild(buttonContainer_2);
        }
        form.setAttribute('action', this.action);
        let response = document.createElement('div');
        response.classList.add('message');
        response.setAttribute('aria-live', 'polite');
        this._errorMessage = response;
        buttonContainer.appendChild(response);
        let hiddenInput = document.createElement('input');
        hiddenInput.type = 'hidden';
        hiddenInput.value = this.action;
        hiddenInput.name = 'action';
        let hiddenInput2 = document.createElement('input');
        hiddenInput2.type = 'hidden';
        hiddenInput2.value = 1;
        hiddenInput2.name = 'current_page';
        this.pageInput = hiddenInput2;
        form.appendChild(hiddenInput2);
        form.appendChild(hiddenInput);
        form.addEventListener('submit', this._submit.bind(this));
        if (!this.hideProgress && this.hideProgress != 'true') {
            this._addIntersectionObserver();
        }
        this._addBlurListener();
    }
    _constructPages() {
        let pages = [];
        let children = this.form.querySelectorAll('allin-paged-form-page');
        if (children.length) {
            for (let i = 0; i < children.length; i++) {
                pages.push({
                    index: i,
                    title: children[i].getAttribute('title'),
                    el: children[i]
                });
                children[i].setAttribute('formname', this.name);
                children[i].setAttribute('uid', this.uid);
                children[i].setAttribute('getSaved', this.getSaved);
            }
        }
        if (this.review) {
            this.review.setAttribute('formname', this.name);
            this.review.setAttribute('uid', this.uid);
            let pagesForReview = pages.map((page, i) => { return { index: i, title: page.title }; });
            this.review.setAttribute('pages', JSON.stringify(pagesForReview));
            pages.push({
                index: pages.length,
                title: this.review.getAttribute('title'),
                el: this.review
            });
        }
        this._pages = pages;
        this._dispatchPageData();
    }
    _addLocationField(id) {
        let hasLocationField = this.form.querySelector('input[name="location_id"]');
        if (hasLocationField) {
            hasLocationField.value = id;
        }
        else {
            let hiddenInput = document.createElement('input');
            hiddenInput.type = 'hidden';
            hiddenInput.value = id;
            hiddenInput.name = 'location_id';
            this.form.appendChild(hiddenInput);
        }
    }
    _addZoneField(id) {
        let hasZoneField = this.form.querySelector('input[name="zone_id"]');
        if (hasZoneField) {
            hasZoneField.value = id;
        }
        else {
            let hiddenInput = document.createElement('input');
            hiddenInput.type = 'hidden';
            hiddenInput.value = id;
            hiddenInput.name = 'zone_id';
            this.form.appendChild(hiddenInput);
        }
    }
    _addOrganisationField(id) {
        let hasOrganisationField = this.form.querySelector('input[name="org_id"]');
        if (hasOrganisationField) {
            hasOrganisationField.value = id;
        }
        else {
            let hiddenInput = document.createElement('input');
            hiddenInput.type = 'hidden';
            hiddenInput.value = id;
            hiddenInput.name = 'org_id';
            this.form.appendChild(hiddenInput);
        }
    }
    _addMembershipField(id) {
        let hasMembershipField = this.form.querySelector('input[name="membership_id"]');
        if (hasMembershipField) {
            hasMembershipField.value = id;
        }
        else {
            let hiddenInput = document.createElement('input');
            hiddenInput.type = 'hidden';
            hiddenInput.value = id;
            hiddenInput.name = 'membership_id';
            this.form.appendChild(hiddenInput);
        }
    }
    _triggerReviewUpdate() {
        if (this.review) {
            let pagesForReview = this._pages.map((page, i) => { return { index: i, title: page.title }; });
            pagesForReview.pop();
            this.review.setPages(pagesForReview);
        }
    }
    _onPageValidity(e) {
        if (e.detail.validity && e.detail.el == this._pages[this._currentPage].el) {
            if (e.detail.el.hasSubPages) {
                let currentSubPage = e.detail.el.getCurrentSubPage();
                let totalSubPages = e.detail.el.subPagesCount();
                if (currentSubPage < totalSubPages - 1) {
                    this.changeSubPage(parseInt(currentSubPage) + 1, this._currentPage);
                }
                else {
                    this.changePage(parseInt(this._currentPage) + 1);
                }
            }
            else {
                this.changePage(parseInt(this._currentPage) + 1);
            }
            window.removeEventListener('allin-page-valid', this._onPageValidity);
        }
        this._nextButton.disabled = false;
    }
    async _onFormValidity(e) {
        if (e.detail.validity && e.detail.el == this._pages[this._currentPage].el) {
            //formData = JSON.stringify(Object.fromEntries(formData));
            this._errorMessage.innerHTML = '';
            this._errorMessage.classList.remove('error');
            this.form.classList.add('loading');
            this._submitButton.disabled = true;
            this.form.setAttribute('aria-busy', 'true');
            try {
                let response = await this._saveToRemoteStorage();
                if (response.ok) {
                    this.form.classList.remove('loading');
                    this.form.setAttribute('aria-busy', 'false');
                    this._submissionSuccess('', response.link ?? null);
                    if (response.redirect) {
                        window.location.href = response.redirect;
                    }
                }
                else {
                    if (response.partial) {
                        this.form.classList.remove('loading');
                        this.form.setAttribute('aria-busy', 'false');
                        this._submissionSuccess(response.message);
                    }
                    else {
                        this._errorMessage.innerHTML = response.message;
                        this._errorMessage.classList.add('error');
                        this.form.classList.remove('loading');
                        this.form.setAttribute('aria-busy', 'false');
                        this._submitButton.disabled = false;
                    }
                }
            }
            catch (error) {
                this._errorMessage.innerHTML = `An error occurred. Please try again later.`;
                this._errorMessage.classList.add('error');
                this.form.classList.remove('loading');
                this._submitButton.disabled = false;
                console.error('Error:', error);
            }
            finally {
                this.form.setAttribute('aria-busy', 'false');
                this._submitButton.disabled = false;
                this.form.classList.remove('loading');
            }
            ;
            window.removeEventListener('allin-form-valid', this._onFormValidity);
        }
    }
    async _onPageValidityForSave(e) {
        if (this._isLastPage) {
            this._handleSubmit(e);
        }
        else {
            this._errorMessage.innerHTML = '';
            this._errorMessage.classList.remove('error');
            this.form.classList.add('loading');
            this._saveButton.disabled = true;
            this.form.setAttribute('aria-busy', 'true');
            try {
                let response = await this._saveToRemoteStorage(false);
                if (response.ok) {
                    this.form.classList.remove('loading');
                    this.form.setAttribute('aria-busy', 'false');
                    //this._errorMessage.innerHTML = msg(str`<allin-dismissable>Progress is saved. You can come back later by visiting your <a href="/organisation-dashboard">organisation manager</a></allin-dismissable>`);
                    let message = '';
                    if (this.name == 'memberssetupform') {
                        message = `\n                            <allin-dismissable>\n                                Progress is saved. You can come back later by visiting your\n                                <a href="${this.hardlinks.dashboard}">membership dashboard</a>\n                            </allin-dismissable>\n                        `;
                    }
                    else {
                        message = `\n                            <allin-dismissable>\n                                Progress is saved. You can come back later by visiting your\n                                <a href="${this.hardlinks.organisation_dashboard}">organisation manager</a>\n                            </allin-dismissable>\n                        `;
                    }
                    this._errorMessage.innerHTML = message;
                    this._errorMessage.classList.add('success');
                    this._saveButton.disabled = false;
                    if (response.id) {
                        this._addLocationField(response.id);
                    }
                    if (response.org_id) {
                        this._addOrganisationField(response.org_id);
                    }
                    if (response.membership_id) {
                        this._addMembershipField(response.membership_id);
                    }
                    if (this.redirectOnSave) {
                        this._clearLocalStorage();
                        setTimeout(() => {
                            window.location.href = this.redirectOnSave;
                        }, 3000);
                    }
                    else {
                        this._saveToLocalStorage();
                        this._errorMessage.scrollIntoView({ behavior: "smooth", block: "start" });
                    }
                }
                else {
                    this._errorMessage.innerHTML = response.message;
                    this._errorMessage.classList.add('error');
                    this.form.classList.remove('loading');
                    this.form.setAttribute('aria-busy', 'false');
                    this._saveButton.disabled = false;
                }
            }
            catch (error) {
                this._errorMessage.innerHTML = `An error occurred. Please try again later.`;
                this._errorMessage.classList.add('error');
                this.form.classList.remove('loading');
                this._saveButton.disabled = false;
                console.error('Error:', error);
            }
            ;
            window.removeEventListener('allin-page-valid', this._onPageValidityForSave);
        }
    }
    async _saveToRemoteStorage(requestApproval = true) {
        let formData = new FormData(this.form);
        if (!requestApproval) {
            formData.append('dontApprove', 'true');
        }
        let response = await fetch(this.restUrl, {
            method: 'POST',
            body: formData,
            headers: {
                'AllIn-Nonce': this.nonce,
            }
        });
        if (response.status === 403) {
            handle403s();
            return;
        }
        response = await response.json();
        return response;
    }
    _handleNextPage(e) {
        e.preventDefault();
        this._validatePage();
        this._nextButton.disabled = true;
        window.addEventListener('allin-page-valid', this._onPageValidity);
    }
    _handlePrevPage(e) {
        e.preventDefault();
        this.changePage(parseInt(this._currentPage) - 1);
    }
    _handleSubmit(e) {
        if (e && typeof e.preventDefault === 'function') {
            e.preventDefault();
        }
        this._validatePage();
        window.addEventListener('allin-form-valid', this._onFormValidity);
        //Scroll to the top upon submit
        const form = document.querySelector('allin-paged-form[name="locationsetupform"][action="add-location"]');
        if (form) {
            form.scrollIntoView({
                behavior: 'smooth',
                block: 'start'
            });
        }
    }
    _handleSave(e) {
        e.preventDefault();
        this._onPageValidityForSave();
    }
    _setSubmitButton() {
        if (this._isLastPage) {
            this._submitButton.style.display = 'block';
            this._submitButton.disabled = false;
            this._nextButton.style.display = 'none';
        }
        else {
            this._submitButton.style.display = 'none';
            this._nextButton.style.display = 'block';
        }
    }
    async _submissionSuccess(partialMessage, link = null) {
        const formData = new FormData(this.form);
        const subscriptionLevel = formData.get('sub_level');
        this._successMessage.subscriptionLevel = subscriptionLevel;
        this._successMessage.orgId = formData.get('org_id');
        window.localStorage.setItem(`allin-paged-form-${this.name}-current-page-${this.uid}`, 'success');
        if (this.form.querySelector('input[name="zone_id"]')) {
            window.localStorage.removeItem(`allin-paged-form-${this.name}-current-page-${this.uid}`);
        }
        window.localStorage.removeItem(`allin-paged-form-${this.name}-${this.uid}`);
        this.eventManager.emit('allin-paged-form-success', { name: this.name, title: this._successMessage.getAttribute('title'), subTitle: this._successMessage.getAttribute('subTitle') });
        this._pages.forEach((page) => {
            page.el.classList.remove('current');
            page.el.classList.add('slide-off');
        });
        this._submitButton.style.display = 'none';
        this._nextButton.style.display = 'none';
        this._backButton.style.display = 'none';
        if (this._saveButton) {
            this._saveButton.style.display = 'none';
        }
        if (partialMessage) {
            let p = document.createElement('p');
            p.innerHTML = partialMessage;
            this._successMessage.appendChild(p);
        }
        if (link) {
            window.localStorage.setItem(`allin-paged-form-${this.name}-current-page-${this.uid}-link`, JSON.stringify(link));
            let a = document.createElement('a');
            a.href = link.url;
            a.target = '_blank';
            a.rel = 'noopener';
            a.classList.add('button');
            a.textContent = link.title;
            this._successMessage.shadowRoot.firstElementChild.appendChild(a);
        }
        this._successMessage.classList.remove('slide-off');
        this._successMessage.classList.add('current');
        let masters = await this._subPageMasters();
        this._successMessage.setSubPageAlerts(masters.dependencies);
        this.renderRoot.host.closest('main').classList.add('success');
        if (this.clearCookies) {
            let cookies = this.clearCookies.split(',');
            cookies.forEach((cookie) => {
                clearCookie(cookie.trim());
            });
        }
        // if(this.redirect){
        //     if(partialMessage){
        //         this.redirect = this.redirect+'?success=partial';
        //     }
        //     setTimeout(()=>{
        //         window.location.href = this.redirect;
        //     },3000);
        // }
        // else{
        //     this.scrollIntoView({behavior: "smooth", block: "start"});
        // }
        // Only redirect to orgnaisation manager when redirect button is Cliked to prevent automatic redirect and give time for the user to read the success message
        if (this.redirect) {
            // Determine redirect URL (add query param if partialMessage exists)
            let redirectUrl = this.redirect;
            if (partialMessage) {
                redirectUrl = redirectUrl + '?success=partial';
            }
            // Create a redirect button
            const redirectButton = document.createElement('button');
            redirectButton.type = 'button';
            redirectButton.classList.add('button', 'redirect-button');
            redirectButton.textContent = 'Visit your organisation manager';
            // Redirect only when clicked manually
            redirectButton.addEventListener('click', () => {
                window.location.href = redirectUrl;
            });
            // Append button to success message container
            this._successMessage.shadowRoot.firstElementChild.appendChild(redirectButton);
        }
        else {
            // Scroll to top if no redirect is set
            this.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }
    _validatePage() {
        let page = this._pages[this._currentPage].el;
        page.setAttribute('validate', true);
    }
    async _saveToLocalStorage() {
        let formData = new FormData(this.form);
        let _formData = Object.fromEntries(formData);
        for (const [key, value] of Object.entries(_formData)) {
            if (value instanceof File) {
                _formData[key] = { file: await this._toBase64(value), name: value.name };
            }
        }
        _formData = JSON.stringify(_formData);
        try {
            window.localStorage.setItem(`allin-paged-form-${this.name}-${this.uid}`, _formData);
        }
        catch (error) {
            //If there is an error loading to local storage, remove the file from the form data as error is probably storage exceeded
            let _formData = Object.fromEntries(formData);
            for (const [key, value] of Object.entries(_formData)) {
                if (value instanceof File) {
                    _formData[key] = '';
                }
            }
            _formData = JSON.stringify(_formData);
            window.localStorage.setItem(`allin-paged-form-${this.name}-${this.uid}`, _formData);
        }
        window.localStorage.setItem(`allin-paged-form-${this.name}-current-page-${this.uid}`, this._currentPage);
    }
    _toBase64(file) {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.readAsDataURL(file);
            reader.onload = () => resolve(reader.result);
            reader.onerror = error => reject(error);
        });
    }
    _clearLocalStorage() {
        window.localStorage.removeItem(`allin-paged-form-${this.name}-${this.uid}`);
        window.localStorage.removeItem(`allin-paged-form-${this.name}-current-page-${this.uid}`);
    }
    _showHideBackButton() {
        if (this._currentPage == 0) {
            this._backButton.style.display = 'none';
        }
        else {
            this._backButton.style.display = 'block';
        }
    }
    async _getRemoteStorage() {
        let response = await fetch(`${this.restUrl}?action=get_form_in_progress&form_name=${this.useRemoteStorage}`, {
            method: 'GET',
            headers: {
                'AllIn-Nonce': this.nonce,
            }
        });
        if (response.status === 403) {
            handle403s();
            return;
        }
        let data = await response.json();
        if (data && data.ok && data.location) {
            this._populateFormData(data.location);
        }
        else if (data && data.ok && data.zone) {
            this._populateFormData(data.zone);
        }
        else if (data && data.ok && data.membership) {
            this._populateFormData(data.membership, true);
        }
    }
    _populateFormData(data, membership = false) {
        if (data.draftInfo) {
            let draftInfo = JSON.parse(data.draftInfo);
            data = { ...data, ...draftInfo };
            delete data.draftInfo;
        }
        if (membership) {
            let local = this._getLocalFormData();
            if (local) {
                Object.keys(local).forEach(key => {
                    if (key.startsWith('provision_')) {
                        data[key] = local[key];
                    }
                });
            }
            delete data.attendingEvents;
            delete data.organisationShares;
            delete data.preferences;
        }
        let _formData = JSON.stringify(data);
        window.localStorage.setItem(`allin-paged-form-${this.name}-${this.uid}`, _formData);
    }
    //NEW METHOD TO GET LOCAL FORM DATA LIKE PROVISION CATEGORIES
    _getLocalFormData() {
        let key = `allin-paged-form-${this.name}-${this.uid}`;
        let data = window.localStorage.getItem(key);
        if (!data)
            return null;
        try {
            return JSON.parse(data);
        }
        catch (e) {
            return null;
        }
    }
    _addIntersectionObserver() {
        const parentHeight = this.form.offsetHeight;
        const saveButtonBox = this._saveButton.getBoundingClientRect();
        this._saveButtonOnScroll.style.width = `${saveButtonBox.width}px`;
        let flag = false;
        if (this._saveButtonOnScroll && parentHeight > window.innerHeight) {
            let footer = document.querySelector("#footer");
            if (window.innerWidth > 1200) {
                //Add an intersection observer to the sidebar
                const observer = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            this._saveButtonOnScroll.classList.add('hide');
                            this._saveButtonOnScroll.classList.remove('fade-in');
                        }
                        else {
                            if (window.scrollY < 200)
                                return;
                            this._saveButtonOnScroll.classList.remove('hide');
                            this._saveButtonOnScroll.classList.add('fade-in');
                            flag = true;
                        }
                    });
                });
                observer.observe(this._saveButton);
                const footerObserver = new IntersectionObserver((entries) => {
                    entries.forEach((entry) => {
                        if (entry.isIntersecting) {
                            this._saveButtonOnScroll.classList.add('hide');
                            this._saveButtonOnScroll.classList.remove('fade-in');
                        }
                        else {
                            if (!flag)
                                return;
                            if (!this._saveButtonOnScroll.classList.contains('hide'))
                                return;
                            if (window.scrollY < 200)
                                return;
                            this._saveButtonOnScroll.classList.remove('hide');
                            setTimeout(() => {
                                this._saveButtonOnScroll.classList.add('fade-in');
                            }, 200);
                        }
                    });
                });
                footerObserver.observe(footer);
            }
        }
    }
    _initSessionTimer() {
        this.interval = setInterval(this.flipSessionVar.bind(this), 10000);
    }
    _refreshUserSession() {
        this.eventManager.emit('refresh-user-session', {});
    }
    flipSessionVar() {
        this.canRefreshSession = !this.canRefreshSession;
    }
    _addBlurListener() {
        this.form.addEventListener('change', this.maybeRefreshOnChange.bind(this));
        //this.DOCUMENT_POSITION_PRECEDING('change',()=>{this.maybeRefreshOnChange()})
    }
    maybeRefreshOnChange() {
        if (this.canRefreshSession) {
            this.canRefreshSession = false;
            window.dispatchEvent(new Event('refresh-user-session'));
            // prevent spamming
            setTimeout(() => {
                this.canRefreshSession = true;
            }, 5000);
        }
    }
    async _checkForValidSubPages() {
        let subpagesMasters = await this._subPageMasters();
        let masters = subpagesMasters.masters;
        let subPages = subpagesMasters.dependencies;
        let parents = subpagesMasters.parents;
        let masterParents = subpagesMasters.masterParents;
        masters.forEach(async (master) => {
            let dependent = subPages.find(sub => sub.getAttribute('dependency') == master.name);
            if (!dependent)
                return;
            await dependent.updateComplete;
            if (master.checked) {
                dependent.setShowState(true);
            }
            else {
                dependent.setShowState(false);
            }
        });
        parents.forEach(parent => {
            if (parent.hasSubPages == true) {
                parent.reprocessSubPages();
            }
            else {
                parent.hasSubPages = true;
            }
        });
        masterParents.forEach(parent => { parent.isMasterPage = true; });
    }
    static styles = [
        accessibility,
        formStyles,
        css `
             #container {
                position: relative;
                min-height: 200px;
            }

            /* Hide form content while loading */
            #container.loading > slot {
                display: none;
            }

            /* Spinner overlay, centered */
            
    `
    ];
}
/*
#container.loading .spinner {
                display: block;
                position: absolute;
                top: 50%;
                left: 36%;
                width: 2rem;
                height: 2rem;
                animation: spin 1s linear infinite;
                z-index: 100;
                background-image: url(/wp-content/themes/ace/assets/wheel.svg);
                background-size: cover;

            /* Hide spinner when not loading
            .spinner {
                display: none;
            }

            /* Spinner animation
            @keyframes spin {
                0% { transform: translate(-50%, -50%) rotate(0deg); }
                100% { transform: translate(-50%, -50%) rotate(360deg); }
            }

            @media screen and (width < 1200px) {
                #container.loading .spinner{
                    left: 50%;
                }
            }
                */ 
